// src/portal/PortalReturnControl.jsx
//
// RETURN THE PLAN. One POST, no body — and the moment the findings become
// visible to the submitter.
//
// ── DISABLED WITH THE REASON, NOT ENABLED WITH A REFUSAL ────────────────────
//
// Route 11 answers `nothing_to_return` when the revision carries no findings:
// "A returned plan must carry at least one finding. Add markup, or approve it."
// The client already knows that from `markup.length`, so §3.19's rule applies —
// the one written for the layer buttons: an enabled control the server has
// already committed to refusing is a round-trip being offered as a choice.
//
// The reason is shown BESIDE the disabled button rather than only in a tooltip,
// for the same reason the layer list shows each refusal inline: a control that
// is dim for an unstated reason reads as broken.
//
// ── THE COUNT IS THE DISCLOSURE, AND IT IS SAID AT THE MOMENT IT HAPPENS ────
//
// `markup_frozen` comes back from route 11 and had no consumer. It is not a
// statistic: every finding it counts was invisible to the submitter a moment
// before and is immutable a moment after. A reviewer who returns a plan is
// entitled to be told what just left their hands.
//
// The confirmation before is the other half. Returning is not reversible from
// this surface — there is no unfreeze, and migration 113's trigger refuses a
// second stamp — so the reviewer is told what will happen while they can still
// decline.

import { useState } from "react";
import { returnPlanReview } from "../api/portalApi";
import { dispositionFor } from "../api/portalRefusals";
import { copyFor } from "../api/portalRefusalCopy";

export default function PortalReturnControl({ planReviewId, markup = [], onReturned, variant = "" }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sentCount, setSentCount] = useState(null);

  // The same predicate route 11 applies: findings on the current revision, at
  // all — frozen or not. `nothing_to_return` counts total, not open.
  const findings = markup.length;
  const canReturn = findings > 0;

  async function doReturn() {
    setBusy(true);
    setFailure(null);
    try {
      const res = await returnPlanReview(planReviewId);
      // THE NUMBER THE SERVER FROZE, not the number we counted before asking.
      // They agree today; if they ever disagree the server is right, and a
      // reviewer told our count would be told a comfortable fiction.
      setSentCount(res?.markup_frozen ?? null);
      setConfirming(false);
      onReturned?.();
    } catch (e) {
      setFailure(copyFor(e, dispositionFor(e)));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  if (sentCount !== null) {
    return (
      <p className="text-xs" data-testid="return-sent">
        Returned. <strong>{sentCount}</strong> finding{sentCount === 1 ? "" : "s"} sent to the submitter.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid="return-control">
      {failure && (
        <div role="alert" className="rounded border border-bg-border bg-bg-soft px-3 py-2">
          <p className="text-sm font-medium">{failure.title}</p>
          {failure.detail && <p className="mt-1 text-xs text-fg-secondary">{failure.detail}</p>}
        </div>
      )}

      {confirming ? (
        <div className="flex flex-col gap-1" data-testid="return-confirm">
          <p className="text-xs">
            Return the plan and send <strong>{findings}</strong> finding{findings === 1 ? "" : "s"}
            {" "}to the submitter? They cannot be changed afterwards.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={doReturn} disabled={busy}
              data-testid="return-confirm-yes"
              className="rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50">
              {busy ? "Returning…" : "Return the plan"}
            </button>
            <button type="button" onClick={() => setConfirming(false)} disabled={busy}
              data-testid="return-confirm-no"
              className="rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50">
              Not yet
            </button>
          </div>
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={!canReturn}
            data-testid="return-plan"
            className={`self-start ${variant || "rounded border border-bg-border px-2 py-1 text-xs disabled:opacity-50"}`}
          >
            Return the plan
          </button>
          {!canReturn && (
            <p className="text-[11px] text-fg-secondary" data-testid="return-blocked-reason">
              A returned plan must carry at least one finding. Add one, or approve the plan.
            </p>
          )}
        </>
      )}
    </div>
  );
}
